import { useCallback, useLayoutEffect, useRef } from 'react'
import { classNames } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { treeStore } from '@/msgdeskRuntime/state'
import type { EditorFormModel, EditorPinModel } from '@/msgdeskRuntime/contracts/editorModel'
import { contract } from '../../Editor.helpers'
import { useEditorFormModelSnapshot } from '../../EditorTemplateContract'
import { useEditorSurface } from '../../EditorSurfaceContext'

const NO_PIN_LABEL = 'No password'
const PARENT_PIN_LABEL = 'Parent. Pin'

function projectPinDom(
  root: HTMLFormElement,
  name: 'show' | 'edit',
  pin: EditorPinModel,
) {
  const inputs = Array.from(root.elements).filter(
    (element): element is HTMLInputElement => element instanceof HTMLInputElement,
  )
  const checkbox = inputs.find(element => element.name === `is_${name}_pin`)
  const input = inputs.find(element => element.name === `${name}_pin`)
  const childCheckbox = inputs.find(element => element.name === (
    name === 'show' ? 'is_one_parent' : 'is_force_pins'
  ))
  if (!checkbox || !input) return

  const inherited = pin.inherited === true
  const enabled = inherited || pin.enabled === true
  const value = inherited
    ? PARENT_PIN_LABEL
    : enabled
      ? pin.value ?? ''
      : NO_PIN_LABEL

  // Legacy still reads these real inputs during save. React's semantic rough
  // is authoritative, while this difference-guarded projector prevents a
  // late update-editor DOM pass from stamping the parent's pins onto a new
  // child editor without changing the existing save transport.
  if (checkbox.checked !== enabled) checkbox.checked = enabled
  if (checkbox.disabled !== inherited) checkbox.disabled = inherited
  if (input.disabled !== (!enabled || inherited)) input.disabled = !enabled || inherited
  if (input.value !== value) input.value = value
  if (childCheckbox) {
    if (inherited && !childCheckbox.checked) childCheckbox.checked = true
    if (!inherited && !enabled && childCheckbox.checked) childCheckbox.checked = false
    const childDisabled = inherited || !enabled
    if (childCheckbox.disabled !== childDisabled) childCheckbox.disabled = childDisabled
  }
}

function projectProtectionDom(root: HTMLFormElement, model: EditorFormModel | undefined) {
  if (!model) return
  projectPinDom(root, 'show', model.pins.show)
  projectPinDom(root, 'edit', model.pins.edit)
}

// Protection fieldset — view/edit pins. The legacy protect form must stay a real
// <form> (it's the protect_form the saver reads), so we keep it but let it carry
// the fieldset panel directly via the skin's display token.
// The checkboxes dispatch the legacy protect toggle (is_to_protect): it
// enables/disables the matching pin input and restores the stored value or the
// "No password" label — the old markup carried this as an inline onclick.
export function ProtectPanel() {
  const { skin } = useEditorSurface()
  const classes = skin.settings
  const model = useEditorFormModelSnapshot()
  const rootRef = useRef<HTMLFormElement | null>(null)

  useLayoutEffect(() => {
    if (rootRef.current) projectProtectionDom(rootRef.current, model)
  }, [model])

  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return
    const observer = new MutationObserver(() => {
      projectProtectionDom(
        root,
        treeStore.getState().templateData.editorFormModel,
      )
    })
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['checked', 'disabled', 'value'],
      subtree: true,
    })
    projectProtectionDom(root, treeStore.getState().templateData.editorFormModel)
    return () => observer.disconnect()
  }, [])

  const assignRootRef = useCallback((node: HTMLFormElement | null) => {
    rootRef.current = node
  }, [])

  return (
    <form
      {...contract('editor.settings.protect')}
      ref={assignRootRef}
      className={classNames('md-settings__protect-form', classes.protectForm)}
    >
      <fieldset className={classes.panel}>
        <legend className={classes.legend}>Protection</legend>
        <div className={classes.row}>
          <label className={classes.rowCheck}>
            <input
              type="checkbox"
              name="is_show_pin"
              value="1"
              onClick={event => dispatchEditorAction({ action: 'toggle-pin', target: event.currentTarget, pin: 'show' })}
            />
            View:
          </label>
          {/* Legacy "( and child )" companion: is_one_parent forces the view
              pin down the sub-chain (m30 reads more_settings[15]). */}
          <label className={classes.rowCheck} title="Force this view pin on children">
            <input type="checkbox" name="is_one_parent" value="1" />
            ( and child )
          </label>
        </div>
        <input type="text" name="show_pin" placeholder="No password" className={classNames('md-settings__protect-input', classes.textInput)} />
        <div className={classes.row}>
          <label className={classes.rowCheck}>
            <input
              type="checkbox"
              name="is_edit_pin"
              value="1"
              onClick={event => dispatchEditorAction({ action: 'toggle-pin', target: event.currentTarget, pin: 'edit' })}
            />
            Edit:
          </label>
          {/* Legacy "( and child )" companion: is_force_pins forces the edit
              pin down the sub-chain (m30 reads more_settings[20]). */}
          <label className={classes.rowCheck} title="Force this edit pin on children">
            <input type="checkbox" name="is_force_pins" value="1" />
            ( and child )
          </label>
        </div>
        <input type="text" name="edit_pin" placeholder="No password" className={classNames('md-settings__protect-input', classes.textInput)} />
      </fieldset>
    </form>
  )
}
